import React, {CSSProperties, useEffect, useState} from "react";
import {Dialog} from "@components/interface/Dialog";
import {selectDialog} from "@redux/selectors/dialog";
import {connect} from "react-redux";
import {IDialog, update as updateDialog} from "@redux/slices/dialog";
import {COLOURS, THEME} from "@components/core/theme";
import {Game} from "@game/game";
import {Update} from "@redux/slices/base";

const styles: Record<string, CSSProperties> = {
    info: {
        ...THEME.text,
        margin: "12px 0",
    },
    label: {
        color: COLOURS.amber,
        fontSize: "small",
    },
    value: {
        fontWeight: "bold",
    },
    buttons: {
        display: "flex",
        gap: 8,
        marginTop: 16,
    },
    button: {
        fontSize: "large",
        padding: "4px 12px",
    },
    status: {
        ...THEME.text,
        marginTop: 12,
        color: COLOURS.forestGreen,
        fontStyle: "italic",
    },
    error: {
        ...THEME.text,
        marginTop: 12,
        color: "red",
        fontStyle: "italic",
    },
};

interface ICompetitionInviteDialog {
    dialog: IDialog
    game: Game
    updateDialog: Update
}

function CompetitionInviteDialog({dialog, game, updateDialog}: ICompetitionInviteDialog) {
    const [compName, setCompName] = useState<string | null>(null);
    const [endsAt, setEndsAt] = useState<string | null>(null);
    const [memberCount, setMemberCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);
    const [joined, setJoined] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [joinError, setJoinError] = useState<string | null>(null);

    const code: string = dialog.data?.code ?? "";

    useEffect(() => {
        if (dialog.name !== "competitionInvite" || !code) return;
        setLoading(true);
        setFetchError(null);
        game.getCompetitionByCode(code)
            .then(info => {
                setCompName(info.name);
                setEndsAt(info.endsAt);
                setMemberCount(info.memberCount);
            })
            .catch(() => setFetchError("Could not load competition details."))
            .finally(() => setLoading(false));
    }, [dialog.name, code]);

    if (dialog.name !== "competitionInvite") return null;

    const handleJoin = async () => {
        setJoining(true);
        setJoinError(null);
        try {
            await game.joinCompetition(code);
            localStorage.removeItem("pendingCompetitionJoin");
            setJoined(true);
            setTimeout(() => updateDialog({update: {}}), 1500);
        } catch {
            setJoinError("Could not join competition. Please try again.");
        } finally {
            setJoining(false);
        }
    };

    const handleDecline = () => {
        localStorage.removeItem("pendingCompetitionJoin");
        updateDialog({update: {}});
    };

    return (
        <Dialog title="Competition Invite" close={false}>
            <div>
                {loading && <div style={styles.info}>Loading…</div>}
                {fetchError && <div style={styles.error}>{fetchError}</div>}
                {!loading && !fetchError && (
                    <div>
                        <div style={styles.info}>
                            <div style={styles.label}>Competition</div>
                            <div style={styles.value}>{compName}</div>
                        </div>
                        <div style={styles.info}>
                            <div style={styles.label}>Ends</div>
                            <div style={styles.value}>{endsAt}</div>
                        </div>
                        <div style={styles.info}>
                            <div style={styles.label}>Members</div>
                            <div style={styles.value}>{memberCount}</div>
                        </div>
                        {joined && <div style={styles.status}>You've joined!</div>}
                        {joinError && <div style={styles.error}>{joinError}</div>}
                        {!joined && (
                            <div style={styles.buttons}>
                                <button style={styles.button} onClick={handleJoin} disabled={joining}>
                                    {joining ? "Joining…" : "Join"}
                                </button>
                                <button style={styles.button} onClick={handleDecline}>
                                    Decline
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Dialog>
    );
}

const mapStateToProps = (store: never) => ({dialog: selectDialog(store)});
const connected = connect(mapStateToProps, {updateDialog})(CompetitionInviteDialog);
export {connected as CompetitionInviteDialog};
